import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../api/api.service';

export type OrgSettings = {
    org_id: string;
    org_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    contact_phone_secondary: string | null;
    contact_website: string | null;
    logo_file_id: string | null;
    logo_name: string | null;
    logo_url: string | null;
    offer_cover_file_id: string | null;
    offer_cover_name: string | null;
    offer_cover_url: string | null;
    offer_footer_text: string | null;
    reservations_enabled: boolean;
    reservations_feature_available: boolean;
    offers_enabled: boolean;
    offers_feature_available: boolean;
    contact_owner_enabled: boolean;
    contact_owner_feature_available: boolean;
    guest_mode_enabled: boolean;
    guest_mode_feature_available: boolean;
    guest_send_message_enabled: boolean;
    guest_send_message_feature_available: boolean;
    guest_request_offer_enabled: boolean;
    guest_request_offer_feature_available: boolean;
};

@Injectable({ providedIn: 'root' })
export class OrgSettingsService {
    private api = inject(ApiService);

    getSettings(): Observable<OrgSettings> {
        return this.api.get<any>('/API2/org/settings').pipe(
            map((resp) => resp?.data || resp || {}),
            map((row) => ({
                org_id: String(row?.org_id || row?.orgId || ''),
                org_name: row?.org_name ?? row?.orgName ?? null,
                contact_email: row?.contact_email ?? row?.contactEmail ?? null,
                contact_phone: row?.contact_phone ?? row?.contactPhone ?? null,
                contact_phone_secondary: row?.contact_phone_secondary ?? row?.contactPhoneSecondary ?? null,
                contact_website: row?.contact_website ?? row?.contactWebsite ?? null,
                logo_file_id: row?.logo_file_id ?? row?.logoFileId ?? null,
                logo_name: row?.logo_name ?? row?.logoName ?? null,
                logo_url: row?.logo_url ?? row?.logoUrl ?? null,
                offer_cover_file_id: row?.offer_cover_file_id ?? row?.offerCoverFileId ?? null,
                offer_cover_name: row?.offer_cover_name ?? row?.offerCoverName ?? null,
                offer_cover_url: row?.offer_cover_url ?? row?.offerCoverUrl ?? null,
                offer_footer_text: row?.offer_footer_text ?? row?.offerFooterText ?? null,
                reservations_enabled: Boolean(row?.reservations_enabled ?? row?.reservationsEnabled ?? true),
                reservations_feature_available: Boolean(
                    row?.reservations_feature_available ?? row?.reservationsFeatureAvailable ?? false
                ),
                offers_enabled: Boolean(row?.offers_enabled ?? row?.offersEnabled ?? true),
                offers_feature_available: Boolean(
                    row?.offers_feature_available ?? row?.offersFeatureAvailable ?? false
                ),
                contact_owner_enabled: Boolean(row?.contact_owner_enabled ?? row?.contactOwnerEnabled ?? true),
                contact_owner_feature_available: Boolean(
                    row?.contact_owner_feature_available ?? row?.contactOwnerFeatureAvailable ?? false
                ),
                guest_mode_enabled: Boolean(row?.guest_mode_enabled ?? row?.guestModeEnabled ?? true),
                guest_mode_feature_available: Boolean(row?.guest_mode_feature_available ?? row?.guestModeFeatureAvailable ?? false),
                guest_send_message_enabled: Boolean(row?.guest_send_message_enabled ?? row?.guestSendMessageEnabled ?? true),
                guest_send_message_feature_available: Boolean(row?.guest_send_message_feature_available ?? row?.guestSendMessageFeatureAvailable ?? false),
                guest_request_offer_enabled: Boolean(row?.guest_request_offer_enabled ?? row?.guestRequestOfferEnabled ?? true),
                guest_request_offer_feature_available: Boolean(row?.guest_request_offer_feature_available ?? row?.guestRequestOfferFeatureAvailable ?? false)
            }))
        );
    }

    updateSettings(payload: {
        org_name?: string | null;
        contact_email?: string | null;
        contact_phone?: string | null;
        contact_phone_secondary?: string | null;
        contact_website?: string | null;
        offer_footer_text?: string | null;
        reservations_enabled?: boolean;
        offers_enabled?: boolean;
        contact_owner_enabled?: boolean;
        guest_mode_enabled?: boolean;
        guest_send_message_enabled?: boolean;
        guest_request_offer_enabled?: boolean;
    }): Observable<any> {
        return this.api.put<any>('/API2/org/settings', payload);
    }

    setLogo(fileId: string): Observable<any> {
        return this.api.post<any>('/API2/org/settings/logo', { file_id: fileId });
    }

    setOfferCover(fileId: string | null): Observable<any> {
        return this.api.post<any>('/API2/org/settings/offer-cover', { file_id: fileId });
    }
}
